/*
  Cedarfur — Mobile Sticky ATC Bar JS
  File: assets/cedarfur-sticky-atc.js
  Load deferred, once, from layout/theme.liquid (gated on template === 'product').
  Zero dependencies — no jQuery, no framework. Bails gracefully if any required
  element is missing.

  Runtime contract:
    - #satc-bar              The sticky bar wrapper (this snippet)
    - .satc__pill[data-satc-tier]   The 3 bundle pills
    - #satc-atc              The CTA button
    - .satc__price           Price readout span
    - window.cfBundles       Bundle product JSON (set by main-product-cedarfur.liquid)
    - window.cfCart          Cart API helper (renderDrawer, getCart, getLastCart)
    - window.cfCartDrawer    Cart drawer opener
    - window.cfReResolveVariant  Variant resolver from cedarfur-pdp.js.liquid
    - #cf-variant-id         Hidden variant input in #cf-product-form
    - input[name="cf-bundle-tier"]  Main bundle radios (for two-way sync)
*/

(function () {
  'use strict';

  /* ================================================================
     STICKY ATC BAR
  ================================================================ */
  (function stickyAtcBar() {

    /* ----- Config ----- */
    var PRICES = {
      '1pack': '$79.99',
      '2pack': '$69.99',
      '3pack': '$107.99'
    };
    var DEFAULT_TIER = '2pack';

    /* ----- State ----- */
    var currentTier = DEFAULT_TIER;
    var isSubmitting = false;

    /* ----- Element references (resolved in init) ----- */
    var bar, pills, priceEl, ctaBtn, colorSel;

    /* -------------------------------------------------------
       TIER SELECTION
       Updates pill active state, price readout, and
       keeps the main buy-box bundle radios in sync.
    ------------------------------------------------------- */
    function selectTier(tier) {
      if (!PRICES[tier]) return;
      currentTier = tier;

      // Update pills
      if (pills) {
        for (var i = 0; i < pills.length; i++) {
          var active = pills[i].getAttribute('data-satc-tier') === tier;
          pills[i].classList.toggle('satc__pill--active', active);
          pills[i].setAttribute('aria-pressed', active ? 'true' : 'false');
        }
      }

      // Update price readout
      if (priceEl) priceEl.textContent = PRICES[tier];

      // Two-way sync: check the corresponding main bundle radio
      // This lets cfReResolveVariant pick up the right tier when we need
      // the variant ID. It also keeps the buy-box visually consistent
      // if the user scrolls back up after using the bar.
      var mainRadio = document.querySelector('input[name="cf-bundle-tier"][value="' + tier + '"]');
      if (mainRadio && !mainRadio.checked) {
        mainRadio.checked = true;
        try {
          mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          var evt = document.createEvent('HTMLEvents');
          evt.initEvent('change', true, false);
          mainRadio.dispatchEvent(evt);
        }
      }

      // Re-resolve variant ID for the now-selected tier + active color
      if (typeof window.cfReResolveVariant === 'function') {
        window.cfReResolveVariant();
      }
    }

    /* -------------------------------------------------------
       VARIANT ID — reads whatever the main form resolved
       (cfReResolveVariant writes it into #cf-variant-id)
    ------------------------------------------------------- */
    function getVariantId() {
      var el = document.getElementById('cf-variant-id');
      return el ? el.value : '';
    }

    /* -------------------------------------------------------
       OPTIMISTIC CART (mirrors productFormHandler pattern)
       Builds a synthetic cart so the drawer opens immediately.
    ------------------------------------------------------- */
    function buildOptimisticCart(variantId) {
      if (!window.cfCart || typeof window.cfCart.getLastCart !== 'function') return null;
      var base = window.cfCart.getLastCart() || { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0 };
      var next;
      try { next = JSON.parse(JSON.stringify(base)); } catch (e) { return null; }

      // Strip any previously-injected optimistic lines
      next.items = (next.items || []).filter(function (i) {
        return !(i.key && i.key.indexOf('optimistic-') === 0);
      });

      var bundle = window.cfBundles && window.cfBundles[currentTier];
      if (!bundle) return null;

      var variant = null;
      if (Array.isArray(bundle.variants)) {
        variant = bundle.variants.find(function (v) {
          return String(v.id) === String(variantId);
        }) || bundle.variants[0];
      }

      var hero = document.getElementById('cf-hero-img');
      var heroSrc = (variant && variant.featured_image && variant.featured_image.src) ||
                    bundle.featured_image || (hero ? hero.src : '');
      var price = (variant && variant.price) || bundle.price || 0;

      var line = {
        key: 'optimistic-bundle-' + variantId,
        id: parseInt(variantId, 10),
        variant_id: parseInt(variantId, 10),
        handle: bundle.handle,
        product_handle: bundle.handle,
        url: '/products/' + bundle.handle,
        quantity: 1,
        title: bundle.title + (variant && variant.title && variant.title !== 'Default Title' ? ' - ' + variant.title : ''),
        product_title: bundle.title,
        variant_title: variant && variant.title !== 'Default Title' ? variant.title : '',
        image: heroSrc,
        price: price,
        original_price: price,
        line_price: price,
        original_line_price: price,
        final_line_price: price,
        properties: {}
      };

      next.items.push(line);
      var totalPrice = 0, itemCount = 0;
      next.items.forEach(function (i) {
        totalPrice += (i.final_line_price != null) ? i.final_line_price : (i.line_price || 0);
        itemCount += (i.quantity || 0);
      });
      next.total_price = totalPrice;
      next.items_subtotal_price = totalPrice;
      next.item_count = itemCount;
      return next;
    }

    /* -------------------------------------------------------
       OPEN DRAWER
    ------------------------------------------------------- */
    function openDrawerWith(cart) {
      if (window.cfCart && typeof window.cfCart.renderDrawer === 'function') {
        try { window.cfCart.renderDrawer(cart); } catch (e) {}
      }
      if (window.cfCartDrawer && typeof window.cfCartDrawer.open === 'function') {
        try { window.cfCartDrawer.open(); } catch (e) {}
      }
    }

    /* -------------------------------------------------------
       ADD TO CART
       Same fetch path as productFormHandler. Uses the variant
       ID already resolved by cfReResolveVariant.
    ------------------------------------------------------- */
    function doAddToCart() {
      if (isSubmitting) return;

      var variantId = getVariantId();
      if (!variantId) {
        // Edge case: color never resolved. Attempt re-resolve once.
        if (typeof window.cfReResolveVariant === 'function') {
          window.cfReResolveVariant();
        }
        variantId = getVariantId();
        if (!variantId) {
          // Still empty — can't proceed without a variant
          return;
        }
      }

      isSubmitting = true;
      if (ctaBtn) { ctaBtn.disabled = true; ctaBtn.textContent = 'Adding…'; }

      // Buy-Now: clear the cart first so checkout shows ONLY the selected pack,
      // then add server-side + redirect to checkout.
      var go = function () { window.location.href = '/cart/add?id=' + encodeURIComponent(variantId) + '&quantity=1&return_to=/checkout'; };
      fetch('/cart/clear.js', { method: 'POST' }).then(go, go);
    }

    /* -------------------------------------------------------
       SCROLL VISIBILITY
       Bar slides in after the user scrolls past the buy box.
       We also hide it when the native ATC button is in view
       so the two don't compete.
    ------------------------------------------------------- */
    function setupVisibility() {
      var ticking = false;

      function compute() {
        ticking = false;
        var vh = window.innerHeight;

        // Buy-zone = from the variant selector (#cf-color-picker) down through the
        // free gifts (.bx__bonus-mini). While ANY part of that zone is on screen the
        // real controls are right there, so hide the bar. Show it everywhere else —
        // both ABOVE the zone (top of page, before the variants) and BELOW it
        // (scrolled into "How it works"). This is why it shows early, not late.
        var topEl = document.getElementById('cf-color-picker');
        var botEl = document.querySelector('.bx__bonus-mini') || document.getElementById('cf-atc');

        var zoneInView;
        if (topEl && botEl) {
          var top = topEl.getBoundingClientRect().top;
          var bot = botEl.getBoundingClientRect().bottom;
          zoneInView = (top < vh) && (bot > 0);
        } else {
          var atc = document.getElementById('cf-atc');
          var r = atc && atc.getBoundingClientRect();
          zoneInView = r ? (r.top < vh && r.bottom > 0) : false;
        }

        bar.classList.toggle('satc--visible', !zoneInView);
      }

      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(compute);
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      // Run once on init (page may have loaded mid-scroll)
      onScroll();
    }

    /* -------------------------------------------------------
       SYNC FROM MAIN BUY BOX
       If the user changes the bundle in the main buy box,
       update the sticky bar to match.
    ------------------------------------------------------- */
    function listenToMainPicker() {
      var radios = document.querySelectorAll('input[name="cf-bundle-tier"]');
      for (var i = 0; i < radios.length; i++) {
        (function (radio) {
          radio.addEventListener('change', function () {
            if (radio.checked && PRICES[radio.value]) {
              // Sync pills + price WITHOUT re-checking the radio (already checked)
              currentTier = radio.value;
              if (pills) {
                for (var j = 0; j < pills.length; j++) {
                  var active = pills[j].getAttribute('data-satc-tier') === currentTier;
                  pills[j].classList.toggle('satc__pill--active', active);
                  pills[j].setAttribute('aria-pressed', active ? 'true' : 'false');
                }
              }
              if (priceEl) priceEl.textContent = PRICES[currentTier];
            }
          });
        })(radios[i]);
      }
    }

    /* -------------------------------------------------------
       INIT
    ------------------------------------------------------- */
    function init() {
      bar = document.getElementById('satc-bar');
      if (!bar) return; // Not on PDP — bail.

      pills = bar.querySelectorAll('.satc__pill[data-satc-tier]');
      priceEl = bar.querySelector('.satc__price');
      ctaBtn = document.getElementById('satc-atc');
      colorSel = document.getElementById('satc-color');

      // Required elements check
      if (!pills.length || !priceEl || !ctaBtn) return;

      // Reveal the bar (HTML has [hidden] to prevent flash of unstyled bar
      // before JS runs; we remove it here so CSS transitions can take over)
      bar.removeAttribute('hidden');

      // Wire pill clicks
      for (var i = 0; i < pills.length; i++) {
        (function (pill) {
          pill.addEventListener('click', function () {
            var tier = pill.getAttribute('data-satc-tier');
            if (tier) selectTier(tier);
          });
        })(pills[i]);
      }

      // Wire CTA
      ctaBtn.addEventListener('click', doAddToCart);

      // Set default selection (2-pack matches main buy-box default)
      selectTier(DEFAULT_TIER);

      // Listen for main buy-box changes
      listenToMainPicker();

      // Wire the color selector <-> main color picker (two-way)
      wireColor();

      // Start scroll-based visibility
      setupVisibility();
    }

    /* -------------------------------------------------------
       COLOR SELECTOR  <->  main #cf-color-picker
       The sticky <select> drives the main swatch picker (which owns
       variant resolution); main swatch taps update the <select> back.
    ------------------------------------------------------- */
    function mainPicker() { return document.getElementById('cf-color-picker'); }
    function activeColor() {
      var p = mainPicker(); if (!p) return null;
      var a = p.querySelector('.vpicker__opt--active[data-cf-color]');
      return a ? a.getAttribute('data-cf-color') : null;
    }
    // Set the <select> to a color if that option exists. Setting .value does
    // not refire 'change', so this never loops back into the change handler.
    function setSelectValue(c) {
      if (!colorSel || !c) return;
      for (var i = 0; i < colorSel.options.length; i++) {
        if (colorSel.options[i].value === c) { colorSel.value = c; return; }
      }
    }
    function syncColorFromMain() {
      // Fall back to 'green' (the canonical default the rest of the PDP forces
      // on load) rather than bailing. The Shopify-rendered markup marks the
      // FIRST variant active (which can be blue), and pdp.js corrects it to
      // green during its own init. If we bail when we read a transient/missing
      // active state, the <select> keeps whatever it had and shows the wrong
      // color. Defaulting to green keeps it consistent with the hero + readout.
      setSelectValue(activeColor() || 'green');
    }
    function wireColor() {
      if (!colorSel) return;

      // While we drive the picker FROM the select, suppress the picker's
      // click-sync so it can't read a stale active swatch and revert the
      // select to the previous color (the "needs two clicks" bug). btn.click()
      // is synchronous, so the flag is true for the whole handler chain.
      var suppressSync = false;

      // sticky select -> click the matching main swatch (runs setActive +
      // swapHero + cfReResolveVariant on the main picker).
      colorSel.addEventListener('change', function () {
        var p = mainPicker(); if (!p) return;
        var btn = p.querySelector('[data-cf-color="' + colorSel.value + '"]');
        if (btn) {
          suppressSync = true;
          try { btn.click(); } finally { suppressSync = false; }
        }
      });

      // main swatch tap -> update the sticky select. Read the color from the
      // CLICKED element, not the active class: pdp.js's setActive() and this
      // listener both fire on the same click and their order isn't guaranteed,
      // so reading the active swatch can be one step stale.
      var p = mainPicker();
      if (p) {
        p.addEventListener('click', function (e) {
          if (suppressSync) return;
          var opt = e.target.closest('[data-cf-color]');
          if (opt) setSelectValue(opt.getAttribute('data-cf-color'));
        });
      }

      // Defer the initial sync one frame. Both this bar and pdp.js init on the
      // same DOMContentLoaded; order between them isn't guaranteed. pdp.js
      // forces the active swatch to green during ITS init, so reading the
      // active color synchronously here can capture the server-rendered first
      // variant (blue) before pdp.js settles. A rAF runs after both handlers
      // finish, so we read the settled green state.
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(syncColorFromMain);
      } else {
        syncColorFromMain();
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

  })();

})();
